"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useDispatch } from "react-redux";
import { setIsCollapsed, setIsMobileSidebarOpen } from "@/redux/slices/sidebarSlice";
import { isFullWidthShellPath } from "@/lib/layout/shellSidebarRoutes";
import { consumeKeepShellCompactAfterNav } from "@/lib/layout/shellSidebarLayout";

/**
 * Keeps sidebar open/closed in sync with the route. Lives in the main bundle (not
 * dynamic-imported). useEffect runs after hydration so SSR and the first client
 * render stay aligned (layout-effect dispatches were mismatching sidebar labels).
 *
 * Only auto-opens when the pathname actually changes — clicking the current page
 * icon while collapsed must not expand the menu again.
 */
export default function ShellSidebarRouteSync() {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const prevPathnameRef = useRef(/** @type {string | null} */ (null));

  useEffect(() => {
    if (typeof window === "undefined" || !pathname) return;

    const prev = prevPathnameRef.current;
    const pathChanged = prev !== null && prev !== pathname;
    const isFirstPaint = prev === null;
    prevPathnameRef.current = pathname;

    if (isFullWidthShellPath(pathname)) {
      dispatch(setIsMobileSidebarOpen(false));
      dispatch(setIsCollapsed(true));
      return;
    }

    // Mobile: overlay drawer — never auto-open; keep closed after navigation.
    if (!window.matchMedia("(min-width: 1024px)").matches) {
      if (pathChanged || isFirstPaint) {
        dispatch(setIsMobileSidebarOpen(false));
        dispatch(setIsCollapsed(true));
      }
      return;
    }

    // Compact-rail flyout child: navigate but keep the icon rail collapsed.
    if (consumeKeepShellCompactAfterNav()) {
      dispatch(setIsMobileSidebarOpen(false));
      dispatch(setIsCollapsed(true));
      return;
    }

    // Re-entry on the same path (e.g. clicking the active icon) must not expand.
    if (!isFirstPaint && !pathChanged) {
      return;
    }

    dispatch(setIsMobileSidebarOpen(true));
    dispatch(setIsCollapsed(false));
  }, [pathname, dispatch]);

  return null;
}
